/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { ChevronDown, ChevronRight } from 'lucide-preact';
import { useEffect, useState } from 'preact/hooks';

import useContext from '@/client/context';
import Avatar from '@/client/components/ui/Avatar';
import SidebarNavigationLink from '@/client/components/shared/SidebarNavigationLink';
import { OPS_SHELL_GROUPS, OPS_SHELL_NAVIGATION, type OpsShellArea } from '@/client/catalogs/automation/opsShellNavigation';

/*----------------------------------
- TYPES
----------------------------------*/

export interface AutomationNavigationMenuProps {
    area: OpsShellArea;
    pathname: string;
    sidebarCollapsed: boolean;
    registerNavigationItem: (id: string, label: string) => void;
    variant?: 'desktop' | 'mobile';
    onNavigate?: () => void;
}

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function AutomationNavigationMenu({
    area,
    pathname,
    sidebarCollapsed,
    registerNavigationItem,
    variant = 'desktop',
    onNavigate,
}: AutomationNavigationMenuProps) {
    const { user } = useContext();
    const areaConfig = OPS_SHELL_NAVIGATION[area];
    const [expandedGroups, setExpandedGroups] = useState<Record<OpsShellArea, boolean>>({
        automation: true,
        cpDev: true,
    });

    useEffect(() => {
        for (const group of OPS_SHELL_GROUPS) {
            for (const section of group.entries) {
                registerNavigationItem(section.key, section.label);
            }
        }
    }, [registerNavigationItem]);

    const toggleGroup = (groupKey: OpsShellArea) => {
        setExpandedGroups((current) => ({ ...current, [groupKey]: !current[groupKey] }));
    };

    const userLabel = user?.name || user?.email || 'Admin';
    const userSubLabel = user?.email || areaConfig.subtitle;
    const isDesktopCollapsed = sidebarCollapsed && variant === 'desktop';

    return (
        <nav
            className={`flex h-[calc(100%-4rem)] flex-col justify-between overflow-y-auto py-4 ${
                isDesktopCollapsed ? 'px-2' : 'px-3'
            } ${
                variant === 'mobile' ? 'pb-24' : ''
            }`}
        >
            <div className={`${isDesktopCollapsed ? 'space-y-3' : 'space-y-2'}`}>
                {!isDesktopCollapsed && (
                    <div className="px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        {areaConfig.navigationLabel}
                    </div>
                )}

                {isDesktopCollapsed
                    ? OPS_SHELL_GROUPS.map((group, index) => (
                          <div key={group.key} className="space-y-2">
                              {group.entries.map((section) => (
                                  <SidebarNavigationLink
                                      key={section.key}
                                      label={section.label}
                                      href={section.href}
                                      icon={section.icon}
                                      dataTestId={section.testId}
                                      active={section.matches(pathname)}
                                      collapsed={true}
                                      isMobile={false}
                                      onNavigate={onNavigate}
                                      iconSize={18}
                                  />
                              ))}
                              {index < OPS_SHELL_GROUPS.length - 1 ? (
                                  <div className="mx-3 border-t border-slate-200" />
                              ) : null}
                          </div>
                      ))
                    : OPS_SHELL_GROUPS.map((group) => {
                          const isGroupActive = group.entries.some((section) => section.matches(pathname));
                          const isGroupExpanded = expandedGroups[group.key];
                          return (
                              <div key={group.key}>
                                  <button
                                      onClick={() => toggleGroup(group.key)}
                                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all ${
                                          isGroupActive
                                              ? 'bg-primary-50 text-primary-600'
                                              : 'text-slate-700 hover:bg-primary-50 hover:text-primary-600'
                                      }`}
                                      data-testid={group.testId}
                                      data-active={isGroupActive ? 'true' : 'false'}
                                  >
                                      <group.icon size={20} />
                                      <>
                                          <span className="text-sm flex-1 text-left">{group.label}</span>
                                          {isGroupExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                      </>
                                  </button>

                                  {isGroupExpanded && (
                                      <div className="ml-6 mt-1 space-y-1">
                                          {group.entries.map((section) => {
                                              const active = section.matches(pathname);
                                              return (
                                                  <SidebarNavigationLink
                                                      key={section.key}
                                                      label={section.label}
                                                      href={section.href}
                                                      icon={section.icon}
                                                      dataTestId={section.testId}
                                                      active={active}
                                                      collapsed={false}
                                                      isMobile={variant === 'mobile'}
                                                      onNavigate={onNavigate}
                                                      variant="child"
                                                  />
                                              );
                                          })}
                                      </div>
                                  )}
                              </div>
                          );
                      })}
            </div>

            <div
                className={`border-t border-slate-200 pt-4 ${
                    isDesktopCollapsed ? 'flex justify-center' : 'rounded-2xl bg-slate-50 px-3 py-3'
                }`}
            >
                <div className={`flex items-center gap-3 ${isDesktopCollapsed ? 'flex-col' : ''}`}>
                    <Avatar fallback={userLabel} size={isDesktopCollapsed ? 'md' : 'sm'} />
                    {!isDesktopCollapsed && (
                        <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900">{userLabel}</div>
                            <div className="truncate text-xs text-slate-500">{userSubLabel}</div>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
}
